// AssetDataSections.js - Reusable collapsible data sections extracted from AssetDetails.js
// Created for PRD-43 prototype V2.
//
// Self-contained component that renders the collapsible data sections (subdomains, stale,
// ips, ports, registration, technologies, urls, cloud, vulnerabilities) plus the
// suppression modal. Manages its own state and lazy-loads scan details.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAssetScanDetails } from '../../services/assetService';
import { getEvents } from '../../services/driftService';
import { parseHttpTransactionFromRaw } from '../common/HttpTransactionView';
import HttpTransactionView from '../common/HttpTransactionView';
import AcceptAlarmModal from '../AcceptAlarmModal';
import IgnoreAlarmModal from '../IgnoreAlarmModal';
import '../../pages/AssetDetails.css'; // reuse all existing classes
import './AssetDataSections.css'; // V3 filtering + hide-headers rules

/**
 * @param {object} props
 * @param {object} props.asset
 * @param {string} props.assetId
 * @param {object} [props.enrichment]
 * @param {Array}  [props.suppressions]
 * @param {function} [props.onSuppressionsChange]
 * @param {string[]} [props.visibleSections] - PRD-43 V3: if provided, only these
 *        section keys render (others return null). Use for per-tab rendering.
 * @param {boolean} [props.autoExpand] - PRD-43 V3: if true, visibleSections render
 *        as always-expanded (collapsible header still shows as a [-] non-toggling
 *        label). Defaults to false for V1/V2 where users toggle sections manually.
 * @param {boolean} [props.hideHeaders] - PRD-43 V3: if true, the collapsible header
 *        is hidden entirely — useful when the outer tab already labels the content.
 */
function AssetDataSections({
    asset,
    assetId,
    enrichment,
    suppressions = [],
    onSuppressionsChange,
    onAssetRiskChange,
    visibleSections = null,
    autoExpand = false,
    hideHeaders = false
}) {
    const navigate = useNavigate();
    const location = useLocation();

    // ── State for scan details data ──
    const [scanDetails, setScanDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    // ── State for technology history timeline ──
    const [showTechHistory, setShowTechHistory] = useState(false);
    const [techHistoryEvents, setTechHistoryEvents] = useState([]);
    const [techHistoryLoading, setTechHistoryLoading] = useState(false);

    // ── State for expanded technology entries (per-subdomain breakdown) ──
    const [expandedTechItems, setExpandedTechItems] = useState({});

    // ── State for data sections (9 keys: subdomains, stale, ips, ports, registration,
    //    technologies, urls, cloud, vulnerabilities) ──
    const [expandedDataSections, setExpandedDataSections] = useState({
        subdomains: false,
        stale: false,
        ips: false,
        ports: false,
        registration: false,
        technologies: false,
        urls: false,
        cloud: false,
        vulnerabilities: false
    });

    // PRD-43 V3: autoExpand mode forces visible sections open (overrides state checks below)
    const isVisible = useCallback((key) => {
        if (!visibleSections) return true;
        return visibleSections.includes(key);
    }, [visibleSections]);

    const isExpanded = useCallback((key) => {
        if (autoExpand && isVisible(key)) return true;
        return expandedDataSections[key];
    }, [autoExpand, isVisible, expandedDataSections]);

    // ── Vulnerability filter and expansion state ──
    const [vulnFilter, setVulnFilter] = useState('all');
    const [expandedVulnItems, setExpandedVulnItems] = useState({});
    const [expandedVulnGroups, setExpandedVulnGroups] = useState({});

    // ── PRD-63 WP3: Unified Accept/Ignore modal state ──
    // The inline custom modal is replaced by the shared AcceptAlarmModal /
    // IgnoreAlarmModal components so the disposition UX is identical wherever
    // a user accepts/ignores a finding (drift queue, alarm drawer, asset page).
    // For vuln cards with no alarm row (below alarmSeverityThreshold) the
    // modals fall back to /suppression-create internally — see modal source.
    const [acceptModalOpen, setAcceptModalOpen] = useState(false);
    const [acceptModalFinding, setAcceptModalFinding] = useState(null);
    const [ignoreModalOpen, setIgnoreModalOpen] = useState(false);
    const [ignoreModalFinding, setIgnoreModalFinding] = useState(null);
    const [suppressionSuccess, setSuppressionSuccess] = useState(null);

    // Local error state (the original page used a top-level setError;
    // here we keep it scoped to the component).
    const [error, setError] = useState(null);

    // ── Toggle vulnerability group expansion ──
    const toggleVulnGroup = (groupId) => {
        setExpandedVulnGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
    };

    // ── Check if a finding is suppressed ──
    const isSuppressed = useCallback((findingType, identifier, matchedAt) => {
        if (!suppressions || suppressions.length === 0) return null;
        const now = Date.now();
        const matches = suppressions.filter(s =>
            s.findingType === findingType &&
            s.findingIdentifier === identifier &&
            s.status === 'active' &&
            (!s.expiresAt || new Date(s.expiresAt).getTime() > now)
        );
        if (matches.length === 0) return null;
        // Prefer scoped match if matchedAt provided
        if (matchedAt) {
            const scoped = matches.find(s => s.matchedAt === matchedAt);
            if (scoped) return scoped;
        }
        return matches[0];
    }, [suppressions]);

    // ── Load technology change history from drift events ──
    const loadTechHistory = useCallback(async () => {
        if (techHistoryLoading) return;
        setTechHistoryLoading(true);
        try {
            const allEvents = await getEvents(assetId);
            // Filter to technology-related rule events
            const techEvents = (allEvents || []).filter(e =>
                e.rulePack === 'Technology Monitoring' ||
                e.ruleName?.toLowerCase().includes('technology') ||
                e.ruleName?.toLowerCase().includes('tech ')
            );
            // Sort newest first
            techEvents.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setTechHistoryEvents(techEvents);
        } catch (err) {
            console.error('Failed to load tech history:', err);
            setTechHistoryEvents([]);
        } finally {
            setTechHistoryLoading(false);
        }
    }, [assetId, techHistoryLoading]);

    // ── Load scan details for data sections (lazy on first expand) ──
    const loadScanDetails = useCallback(async () => {
        if (scanDetails || !asset?.lastResultId || detailsLoading) return;
        setDetailsLoading(true);
        try {
            const data = await getAssetScanDetails(assetId, asset.lastResultId);
            setScanDetails(data);
        } catch (err) {
            console.error('Failed to load scan details:', err);
        } finally {
            setDetailsLoading(false);
        }
    }, [assetId, asset?.lastResultId, scanDetails, detailsLoading]);

    // ── PRD-43 V3: auto-load scan details when autoExpand is set ──
    // The V3 tab system renders sections pre-expanded (autoExpand={true}),
    // bypassing the manual toggle that normally triggers loadScanDetails().
    useEffect(() => {
        if (autoExpand && visibleSections && !scanDetails && !detailsLoading && asset?.lastResultId) {
            loadScanDetails();
        }
    }, [autoExpand, visibleSections, scanDetails, detailsLoading, asset?.lastResultId, loadScanDetails]);

    // ── Handle hash-based navigation for deep-linking to sections ──
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (!hash || !asset) return;

        // Parse hash - could be "subdomains", "vulnerabilities-critical", etc.
        const [section, severity] = hash.split('-');
        const validSections = ['subdomains', 'stale', 'ips', 'ports', 'registration', 'technologies', 'urls', 'cloud', 'vulnerabilities'];

        if (validSections.includes(section)) {
            // Set vulnerability filter if specified
            if (section === 'vulnerabilities' && severity) {
                setVulnFilter(severity);
            }

            // Expand the section
            setExpandedDataSections(prev => ({ ...prev, [section]: true }));

            // Load scan details if needed
            loadScanDetails();

            // Scroll to section after a short delay
            setTimeout(() => {
                const element = document.getElementById(`data-section-${section}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 200);
        }
    }, [location.hash, asset, loadScanDetails]);

    // ── Toggle data section and load details if needed ──
    const toggleDataSection = (section) => {
        const newState = !expandedDataSections[section];

        // Load details on first expand
        if (newState && !scanDetails) {
            loadScanDetails();
        }

        setExpandedDataSections(prev => ({ ...prev, [section]: newState }));

        // Update URL hash via replace navigation. We preserve the rest of the path
        // by using location.pathname instead of hard-coding /assets/${assetId}.
        if (newState) {
            navigate(`${location.pathname}#${section}`, { replace: true });
        } else {
            navigate(location.pathname, { replace: true });
        }
    };

    // ── Extract data from scan details ──
    // Check discoveryData first (merged from inventory for vuln-scans), then stageResults
    const getStageData = () => {
        if (!scanDetails?.fullResult) return {};

        const fr = scanDetails.fullResult;
        const discoveryData = fr.discoveryData || {};
        const stageResults = fr.stageResults || {};

        // Legacy stage data (for discovery/baseline scans)
        const dnsStage = stageResults.dnsEnumeration || {};
        const ipStage = stageResults.ipResolution || {};
        const portStage = stageResults.portScanning || {};
        const webStage = stageResults.webCrawling || {};
        const vulnStage = stageResults.vulnScanning || {};
        const cloudStage = stageResults.cloudEnumeration || {};

        // Prefer discoveryData (from inventory) for vuln-scans, fall back to stageResults
        return {
            // Subdomains
            subdomains: discoveryData.subdomains?.length ? discoveryData.subdomains : (dnsStage.subdomains || []),
            liveSubdomains: discoveryData.liveSubdomains?.length ? discoveryData.liveSubdomains : (dnsStage.liveSubdomains || []),

            // IPs
            ips: discoveryData.ips?.length ? discoveryData.ips : (ipStage.ips || ipStage.resolvedIps || []),
            ipDetails: ipStage.ipDetails || [],

            // Ports and Services
            ports: discoveryData.ports?.length ? discoveryData.ports : (portStage.openPorts || portStage.ports || []),
            services: discoveryData.services?.length ? discoveryData.services
                : fr.inventory?.services?.length ? fr.inventory.services
                : (portStage.services || portStage.nmapServices || fr.services || []),
            highRiskServices: portStage.highRiskServices || [],

            // URLs
            urls: discoveryData.urls?.length ? discoveryData.urls
                : fr.inventory?.urls?.length ? fr.inventory.urls
                : (webStage.liveUrls || webStage.urls || webStage.endpoints || []),
            crawledUrls: webStage.crawledUrls || [],
            dynamicUrls: webStage.dynamicUrls || [],
            apiUrls: webStage.apiUrls || [],
            interestingUrls: webStage.interestingUrls || [],
            webServers: webStage.webServers || [],
            technologies: discoveryData.technologies?.length ? discoveryData.technologies
                : fr.inventory?.technologies?.length ? fr.inventory.technologies
                : (webStage.technologies || []),
            normalizedTechnologies: discoveryData.normalizedTechnologies || [],
            techSummary: discoveryData.techSummary || fr.inventory?.techSummary || null,
            techAnalysis: discoveryData.techAnalysis || fr.inventory?.techAnalysis || null,

            // Vulnerabilities - from vuln-scan findings or legacy vulnStage
            vulnerabilities: fr.findings || vulnStage.vulnerabilities || vulnStage.findings || [],

            // Cloud enumeration data — PRD-83 Phase B: entity-first resolver.
            // The asset entity is now authoritative for top-level cloud flags
            // (azureTenantId / *Detected / cloudflareWafEnabled), so prior-scan
            // discoveries survive even when `lastResultId` later points at a
            // blob without cloudDiscovery (e.g. a deep-security snapshot). The
            // blob remains authoritative for the raw container/resource lists.
            cloud: (() => {
                const vulnCloud = fr.cloudDiscovery || {};
                const safeParse = (s) => {
                    if (!s) return [];
                    try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; }
                    catch { return []; }
                };
                const entityTenants = safeParse(asset?.azureTenantsJson);
                const entityResources = safeParse(asset?.azureResourcesJson);
                const entityHasCloud = !!(asset?.azureTenantId
                    || asset?.azureDetected
                    || asset?.awsDetected
                    || asset?.gcpDetected
                    || asset?.cloudflareWafEnabled
                    || entityTenants.length
                    || entityResources.length);

                if (entityHasCloud || vulnCloud.hasCloudPresence || vulnCloud.azure?.detected || vulnCloud.aws?.detected || vulnCloud.gcp?.detected) {
                    const vAzure = vulnCloud.azure || {};
                    const vAws = vulnCloud.aws || {};
                    const vGcp = vulnCloud.gcp || {};
                    const vCf = vulnCloud.cloudflare || {};
                    return {
                        azure: {
                            // Entity-first for top-level flags; blob lists pass through.
                            tenantId: asset?.azureTenantId || vAzure.tenantId || null,
                            tenants: entityTenants.length ? entityTenants : (vAzure.tenants || []),
                            resources: entityResources.length ? entityResources : (vAzure.resources || []),
                            detected: asset?.azureDetected || vAzure.detected || false,
                            containers: vAzure.containers || [],
                            findings: vAzure.findings || []
                        },
                        aws: {
                            detected: asset?.awsDetected || vAws.detected || false,
                            buckets: vAws.buckets || [],
                            services: vAws.services || [],
                            findings: vAws.findings || []
                        },
                        gcp: {
                            detected: asset?.gcpDetected || vGcp.detected || false,
                            buckets: vGcp.buckets || [],
                            projects: vGcp.projects || [],
                            findings: vGcp.findings || []
                        },
                        cloudflare: {
                            detected: vCf.detected || false,
                            wafEnabled: asset?.cloudflareWafEnabled || vCf.wafEnabled || false,
                            findings: vCf.findings || []
                        },
                        summary: vulnCloud.summary || { total: 0, awsCount: 0, azureCount: 0, gcpCount: 0, azureTenantCount: 0, publicReadCount: 0, publicWriteCount: 0, providers: [], services: [] }
                    };
                }
                // Fall back to discovery data
                if (discoveryData.cloudEnumeration) {
                    return discoveryData.cloudEnumeration;
                }
                // Finally fall back to legacy stage results
                return {
                    azure: cloudStage.azure || { containers: [], tenants: [], tenantId: null, detected: false },
                    aws: cloudStage.aws || { buckets: [], detected: false },
                    gcp: cloudStage.gcp || { buckets: [], detected: false },
                    summary: cloudStage.summary || { total: 0, awsCount: 0, azureCount: 0, gcpCount: 0, azureTenantCount: 0, publicReadCount: 0, publicWriteCount: 0 }
                };
            })(),

            // Stale subdomains - from inventory aging system
            staleSubdomains: discoveryData.staleSubdomains || [],

            // Metadata
            wafDetected: discoveryData.wafDetected || fr.wafDetected || false,
            wafType: discoveryData.wafType || fr.wafType || null,
            lastDiscoveryAt: discoveryData.lastDiscoveryAt || null
        };
    };

    // ── Calculate vulnerability counts (cumulative from asset entity) ──
    const vulnCounts = useMemo(() => {
        if (asset) {
            return {
                critical: asset.criticalCount || 0,
                high: asset.highCount || 0,
                medium: asset.mediumCount || 0,
                low: asset.lowCount || 0,
                info: asset.infoCount || 0,
                total: (asset.criticalCount || 0) + (asset.highCount || 0) +
                       (asset.mediumCount || 0) + (asset.lowCount || 0) + (asset.infoCount || 0)
            };
        }
        return { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: 0 };
    }, [asset]);

    // ── Consolidate similar vulnerabilities ──
    const consolidateVulnerabilities = useCallback((vulns) => {
        // Step 1: Deduplicate exact duplicates first
        const seenKeys = new Set();
        const deduplicated = [];

        vulns.forEach(v => {
            // Create unique key from templateId + matchedAt (or matched)
            const templateId = v.templateId || v.id || v.name || '';
            const matched = v.matchedAt || v.matched || v.url || v.host || '';
            const dedupeKey = `${templateId}::${matched}`;

            if (!seenKeys.has(dedupeKey)) {
                seenKeys.add(dedupeKey);
                // Transform vuln to add httpTransaction from raw data if available
                const transformed = { ...v };
                if (!transformed.httpTransaction && transformed.raw) {
                    transformed.httpTransaction = parseHttpTransactionFromRaw(transformed.raw, matched);
                }
                deduplicated.push(transformed);
            }
        });

        // Step 2: Group similar vulnerabilities
        const groups = {};
        const standalone = [];

        deduplicated.forEach(v => {
            const name = v.name || v.title || v.id || 'Unknown';
            const severity = (v.severity || v.priority || 'info').toLowerCase();

            // Check if this is a groupable vulnerability type
            let groupKey = null;
            let groupName = null;

            if (name.toLowerCase().includes('missing security header') ||
                name.toLowerCase().includes('http missing')) {
                groupKey = `missing-security-headers-${severity}`;
                groupName = 'HTTP Missing Security Headers';
            } else if (name.toLowerCase().includes('cookie without') ||
                       name.toLowerCase().includes('cookie attribute')) {
                groupKey = `cookie-issues-${severity}`;
                groupName = 'Cookie Security Issues';
            } else if (name.toLowerCase().includes('cors') ||
                       name.toLowerCase().includes('cross-origin')) {
                groupKey = `cors-issues-${severity}`;
                groupName = 'CORS Configuration Issues';
            }

            if (groupKey) {
                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        groupName,
                        severity,
                        items: [],
                        isGroup: true
                    };
                }
                groups[groupKey].items.push(v);
            } else {
                standalone.push(v);
            }
        });

        const consolidated = [];

        // Add groups first (they often represent multiple findings)
        Object.values(groups).forEach(group => {
            consolidated.push({
                name: `${group.groupName} (${group.items.length})`,
                severity: group.severity,
                isGroup: true,
                groupItems: group.items,
                host: group.items[0]?.host || '',
                matchedAt: `${group.items.length} locations`,
                description: `${group.items.length} security headers missing or misconfigured. Click to expand and view details.`,
                tags: [...new Set(group.items.flatMap(i => i.tags || []))].slice(0, 5)
            });
        });

        // Add standalone items
        consolidated.push(...standalone);

        return consolidated;
    }, []);

    // ── Get severity color ──
    const getSeverityColor = (severity) => {
        const colors = {
            'critical': '#FF0000',
            'high': '#FF6B35',
            'medium': '#FFB800',
            'low': '#39FF14',
            'info': '#00D9FF'
        };
        return colors[severity?.toLowerCase()] || '#808080';
    };

    // ── Toggle vulnerability item expansion ──
    const toggleVulnItem = (vulnId) => {
        setExpandedVulnItems(prev => ({ ...prev, [vulnId]: !prev[vulnId] }));
    };

    // ── PRD-63 WP3: open unified accept/ignore modal for a vuln card ──
    // Builds the `finding` shape the modal needs (findingType, findingIdentifier,
    // etc.) so its below-threshold createSuppression() fallback has every field
    // /suppression-create requires. assetId comes from props (no recomputation).
    const openDispositionModal = (disposition, type, identifier, name, vuln, e) => {
        if (e) e.stopPropagation();
        const findingPayload = {
            // No alarmId — vuln cards represent raw findings, not alarm rows.
            // This drives the modals' dual-route logic to use suppression-create.
            findingType: type,
            findingIdentifier: identifier,
            findingName: name,
            findingId: vuln?.findingId || null,
            matchedAt: vuln?.host || vuln?.matchedAt || vuln?.url || null,
            severity: vuln?.severity || null,
            name,
        };
        if (disposition === 'accept') {
            setAcceptModalFinding(findingPayload);
            setAcceptModalOpen(true);
        } else {
            setIgnoreModalFinding(findingPayload);
            setIgnoreModalOpen(true);
        }
    };

    // Both modals call this on success. The unified-modal response shape is
    // { success, ..., assetRisk } from /suppression-create OR { success,
    // suppression, assetRisk, ... } from /drift-alarms/{id}/accept|ignore.
    // Either way, bubble assetRisk up for the parent's risk-card refresh and
    // re-fetch the suppressions list so the inline badge flips immediately.
    const handleDispositionSuccess = (result, dispositionLabel, name) => {
        if (result?.assetRisk && typeof onAssetRiskChange === 'function') {
            onAssetRiskChange(result.assetRisk);
        }
        // Refresh parent suppressions list — passes the new suppression entity
        // when present, otherwise the whole response (parent's setter is
        // resilient to either shape since it always re-reads from the API).
        if (typeof onSuppressionsChange === 'function') {
            onSuppressionsChange(result?.suppression || result);
        }
        setSuppressionSuccess(`${dispositionLabel}: ${name}`);
        setTimeout(() => setSuppressionSuccess(null), 3000);
    };

    if (!asset) return null;

    // Use calculated vulnCounts for consistency
    const totalVulns = vulnCounts.total;

    return (
        <>
            {error && (
                <div className="error-banner" style={{ marginBottom: '8px' }}>
                    {error}
                    <button onClick={() => setError(null)} style={{ marginLeft: '8px' }}>x</button>
                </div>
            )}
            {suppressionSuccess && (
                <div className="success-banner" style={{ marginBottom: '8px' }}>
                    {suppressionSuccess}
                </div>
            )}

            <div
                className={`data-sections${visibleSections ? ' data-sections--filtered' : ''}${hideHeaders ? ' data-sections--hide-headers' : ''}`}
                data-visible={visibleSections ? visibleSections.join(' ') : undefined}
            >
                {/* Discovered Subdomains Section */}
                <div className="data-section" id="data-section-subdomains">
                    <div
                        className="data-section-header"
                        onClick={() => toggleDataSection('subdomains')}
                    >
                        <h3 className="data-section-title">
                            <span className="section-icon">🌐</span>
                            Subdomains
                            <span className="section-count live-count">({getStageData().liveSubdomains?.length || asset.liveSubdomains || 0} live)</span>
                        </h3>
                        <span className="section-toggle">{isExpanded('subdomains') ? '[-]' : '[+]'}</span>
                    </div>
                    {isExpanded('subdomains') && (
                        <div className="data-section-content">
                            {detailsLoading ? (
                                <div className="section-loading">Loading subdomain data...</div>
                            ) : (
                                <>
                                    {(() => {
                                        const data = getStageData();
                                        const allSubdomains = data.subdomains || [];
                                        const liveSubdomains = data.liveSubdomains || [];
                                        const liveSet = new Set(liveSubdomains);

                                        // Sort: live subdomains first, then historical
                                        const sortedSubdomains = [
                                            ...liveSubdomains,
                                            ...allSubdomains.filter(s => !liveSet.has(s))
                                        ];
                                        const historicalCount = allSubdomains.length - liveSubdomains.length;

                                        return liveSubdomains.length > 0 || allSubdomains.length > 0 ? (
                                            <>
                                                <div className="section-summary">
                                                    <span className="summary-stat live">Live (DNS Validated): <strong>{liveSubdomains.length}</strong></span>
                                                    {historicalCount > 0 && (
                                                        <span className="summary-stat historical">Historical (Passive): <strong>{historicalCount}</strong></span>
                                                    )}
                                                </div>
                                                {historicalCount > 0 && (
                                                    <div className="subdomain-note">
                                                        Historical subdomains are from passive sources (CT logs, DNS datasets) and may no longer exist.
                                                    </div>
                                                )}
                                                <div className="data-list">
                                                    {sortedSubdomains.slice(0, 50).map((sub, i) => (
                                                        <div key={i} className={`data-item ${liveSet.has(sub) ? 'live' : 'historical'}`}>
                                                            <span className="data-value">{sub}</span>
                                                            {liveSet.has(sub) ? (
                                                                <span className="live-badge">LIVE</span>
                                                            ) : (
                                                                <span className="historical-badge">HISTORICAL</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {sortedSubdomains.length > 50 && (
                                                        <div className="data-more">...and {sortedSubdomains.length - 50} more</div>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="section-empty">No subdomain data available. Run a scan to discover subdomains.</div>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Stale Subdomains Section - Shows subdomains not seen in 2+ weekly discoveries */}
                {(() => {
                    const data = getStageData();
                    const staleSubdomains = data.staleSubdomains || [];
                    const trulyStale = staleSubdomains.filter(s => (s.missedScans || 0) >= 2);
                    const pendingStale = staleSubdomains.filter(s => (s.missedScans || 0) === 1);
                    const hasStale = staleSubdomains.length > 0;

                    if (!hasStale) return null;

                    return (
                        <div className="data-section stale-section" id="data-section-stale">
                            <div
                                className="data-section-header stale-header"
                                onClick={() => toggleDataSection('stale')}
                            >
                                <h3 className="data-section-title">
                                    <span className="section-icon">👻</span>
                                    Stale Subdomains
                                    <span className="section-count stale-count">({trulyStale.length} stale{pendingStale.length > 0 ? `, ${pendingStale.length} pending` : ''})</span>
                                </h3>
                                <span className="section-toggle">{isExpanded('stale') ? '[-]' : '[+]'}</span>
                            </div>
                            {isExpanded('stale') && (
                                <div className="data-section-content stale-content">
                                    <div className="stale-explanation">
                                        <span className="info-icon">ℹ️</span>
                                        Subdomains that have not appeared in recent weekly discoveries. Truly stale subdomains have been missing for 2+ weeks.
                                    </div>

                                    {trulyStale.length > 0 && (
                                        <div className="stale-group">
                                            <h4 className="stale-group-title">Truly Stale (2+ weeks missing)</h4>
                                            <div className="data-list stale-list">
                                                {trulyStale.slice(0, 30).map((entry, i) => (
                                                    <div key={i} className="data-item stale-item truly-stale">
                                                        <span className="data-value stale-subdomain">{entry.subdomain}</span>
                                                        <span className="missed-badge">{entry.missedScans}x missed</span>
                                                        {entry.lastSeen && (
                                                            <span className="last-seen">Last seen: {new Date(entry.lastSeen).toLocaleDateString()}</span>
                                                        )}
                                                    </div>
                                                ))}
                                                {trulyStale.length > 30 && (
                                                    <div className="data-more">...and {trulyStale.length - 30} more stale subdomains</div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {pendingStale.length > 0 && (
                                        <div className="stale-group">
                                            <h4 className="stale-group-title pending-title">Pending (1 week missing)</h4>
                                            <p className="pending-note">These may reappear in the next discovery scan.</p>
                                            <div className="data-list stale-list pending-list">
                                                {pendingStale.slice(0, 20).map((entry, i) => (
                                                    <div key={i} className="data-item stale-item pending-stale">
                                                        <span className="data-value stale-subdomain">{entry.subdomain}</span>
                                                        <span className="pending-badge">pending</span>
                                                        {entry.lastSeen && (
                                                            <span className="last-seen">Last seen: {new Date(entry.lastSeen).toLocaleDateString()}</span>
                                                        )}
                                                    </div>
                                                ))}
                                                {pendingStale.length > 20 && (
                                                    <div className="data-more">...and {pendingStale.length - 20} more pending</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* IPs Section */}
                <div className="data-section" id="data-section-ips">
                    <div
                        className="data-section-header"
                        onClick={() => toggleDataSection('ips')}
                    >
                        <h3 className="data-section-title">
                            <span className="section-icon">📍</span>
                            IP Addresses
                            <span className="section-count">({getStageData().ips?.length || asset.ipCount || 0})</span>
                        </h3>
                        <span className="section-toggle">{isExpanded('ips') ? '[-]' : '[+]'}</span>
                    </div>
                    {isExpanded('ips') && (
                        <div className="data-section-content">
                            {detailsLoading ? (
                                <div className="section-loading">Loading IP data...</div>
                            ) : (
                                <>
                                    {(() => {
                                        const data = getStageData();
                                        const ips = data.ips || [];
                                        const ipDetails = data.ipDetails || [];
                                        const fr = scanDetails?.fullResult || {};
                                        const gnData = fr.greynoiseData || {};
                                        return ips.length > 0 ? (
                                            <div className="data-list">
                                                {ips.slice(0, 50).map((ip, i) => {
                                                    const detail = ipDetails.find(d => d.ip === ip) || {};
                                                    const gn = gnData[ip];
                                                    return (
                                                        <div key={i} className="data-item">
                                                            <span className="data-value mono">{ip}</span>
                                                            {detail.asn && <span className="data-tag">{detail.asn}</span>}
                                                            {detail.org && <span className="data-meta">{detail.org}</span>}
                                                            {gn && (
                                                                gn.link ? (
                                                                    <a href={gn.link} target="_blank" rel="noopener noreferrer" className={`gn-badge gn-${gn.classification || 'unknown'}`}>
                                                                        {gn.classification === 'benign' ? '●' : gn.classification === 'malicious' ? '◆' : '○'} {(gn.classification || 'unknown').charAt(0).toUpperCase() + (gn.classification || 'unknown').slice(1)}
                                                                        {gn.name && ` (${gn.name})`}
                                                                    </a>
                                                                ) : (
                                                                    <span className={`gn-badge gn-${gn.classification || 'unknown'}`}>
                                                                        {gn.classification === 'benign' ? '●' : gn.classification === 'malicious' ? '◆' : '○'} {(gn.classification || 'unknown').charAt(0).toUpperCase() + (gn.classification || 'unknown').slice(1)}
                                                                    </span>
                                                                )
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {ips.length > 50 && (
                                                    <div className="data-more">...and {ips.length - 50} more</div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="section-empty">No IP data available. Run a scan to discover IP addresses.</div>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Ports Section */}
                <div className="data-section" id="data-section-ports">
                    <div
                        className="data-section-header"
                        onClick={() => toggleDataSection('ports')}
                    >
                        <h3 className="data-section-title">
                            <span className="section-icon">🔌</span>
                            Ports and Services
                            <span className="section-count">({getStageData().ports?.length || asset.openPortCount || 0})</span>
                        </h3>
                        <span className="section-toggle">{isExpanded('ports') ? '[-]' : '[+]'}</span>
                    </div>
                    {/* PRD-95 Phase C: per-stage note when the last IP/CIDR scan's port
                        stage silently timed out. Gated by the same sticky entity field
                        as the CoverageBanner so the two surfaces stay in sync. */}
                    {(asset.assetType === 'ip' || asset.assetType === 'cidr') && asset.lastPortScanTimeout && (
                        <div className="ads-stage-note ads-stage-note--warn">
                            ⚠ Port scan didn't complete on the last run — the count above and any ports listed below may be incomplete.
                        </div>
                    )}
                    {isExpanded('ports') && (
                        <div className="data-section-content">
                            {detailsLoading ? (
                                <div className="section-loading">Loading port data...</div>
                            ) : (
                                <>
                                    {(() => {
                                        const data = getStageData();
                                        const ports = data.ports || [];
                                        const services = data.services || [];
                                        const highRiskServices = data.highRiskServices || [];

                                        // Build service lookup map with product/version
                                        const serviceMap = new Map();
                                        services.forEach(s => {
                                            const key = s.port || s.portId;
                                            if (key) {
                                                serviceMap.set(parseInt(key), {
                                                    service: s.service || s.name,
                                                    product: s.product || null,
                                                    version: s.version || null,
                                                    protocol: s.protocol
                                                });
                                            }
                                        });

                                        // Parse ports - handles: objects, "IP:port" strings, or plain port numbers
                                        const parsedPorts = ports.map(port => {
                                            if (typeof port === 'object') {
                                                const svcInfo = serviceMap.get(parseInt(port.port)) || {};
                                                return {
                                                    ip: port.ip,
                                                    port: port.port,
                                                    service: port.service || svcInfo.service,
                                                    product: port.product || svcInfo.product,
                                                    version: port.version || svcInfo.version,
                                                    protocol: port.protocol || svcInfo.protocol
                                                };
                                            }
                                            if (typeof port === 'string' && port.includes(':')) {
                                                const [ip, portNum] = port.split(':');
                                                const svcInfo = serviceMap.get(parseInt(portNum)) || {};
                                                return { ip, port: portNum, ...svcInfo };
                                            }
                                            const svcInfo = serviceMap.get(parseInt(port)) || {};
                                            return { ip: null, port: port, ...svcInfo };
                                        });

                                        return parsedPorts.length > 0 || services.length > 0 ? (
                                            <>
                                                {/* High Risk Services Banner */}
                                                {highRiskServices.length > 0 && (
                                                    <div className="high-risk-services-banner">
                                                        <span className="banner-icon">⚠️</span>
                                                        <span className="banner-text">
                                                            {highRiskServices.length} high-risk services detected:
                                                            {highRiskServices.slice(0, 5).map((s, i) => (
                                                                <span key={i} className="high-risk-tag">{s.service || s.name} ({s.port})</span>
                                                            ))}
                                                        </span>
                                                    </div>
                                                )}

                                                <div className="svc-banner-table-wrap">
                                                    <table className="svc-banner-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Port</th>
                                                                <th>Service</th>
                                                                <th>Product</th>
                                                                <th>Version</th>
                                                                <th>Risk</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(parsedPorts.length > 0 ? parsedPorts : services).slice(0, 50).map((item, i) => {
                                                                const portNum = parseInt(item.port || item.portId) || 0;
                                                                const isHighRisk = [21, 22, 23, 25, 3389, 5900, 445, 139, 1433, 3306, 5432, 27017].includes(portNum);
                                                                return (
                                                                    <tr key={i} className={`svc-banner-row ${isHighRisk ? 'svc-banner-high-risk' : ''}`}>
                                                                        <td className="svc-banner-port">{item.port || item.portId}</td>
                                                                        <td className="svc-banner-service">{item.service || item.name || '\u2014'}</td>
                                                                        <td className="svc-banner-product">{item.product || '\u2014'}</td>
                                                                        <td className="svc-banner-version">{item.version || '\u2014'}</td>
                                                                        <td className="svc-banner-risk">{isHighRisk ? <span className="svc-risk-badge">High Risk</span> : 'Normal'}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="section-empty">No open ports detected. Run a scan to discover open ports.</div>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Registration Section (Domain assets only) */}
                {asset.assetType === 'domain' && (() => {
                    const fr = scanDetails?.fullResult || {};
                    const reg = fr.registration || fr.whois || fr.domainIntelligence || {};
                    const hasRegData = reg.registrar || reg.expirationDate || reg.registrationDate;
                    if (!hasRegData) return null;

                    // Calculate days until expiry
                    let daysUntilExpiry = null;
                    let expiryClass = '';
                    if (reg.expirationDate) {
                        const expiry = new Date(reg.expirationDate);
                        if (!isNaN(expiry.getTime())) {
                            daysUntilExpiry = Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24));
                            expiryClass = daysUntilExpiry > 90 ? 'reg-expiry-ok' : daysUntilExpiry > 30 ? 'reg-expiry-warn' : 'reg-expiry-danger';
                        }
                    }

                    const nameservers = reg.nameservers || reg.nameServers || [];
                    const status = reg.status || [];
                    const source = reg.source || 'unknown';

                    return (
                        <div className="data-section" id="data-section-registration">
                            <div
                                className="data-section-header"
                                onClick={() => toggleDataSection('registration')}
                            >
                                <h3 className="data-section-title">
                                    <span className="section-icon">📋</span>
                                    Registration
                                    <span className="section-count">({source.toUpperCase()})</span>
                                </h3>
                                <span className="section-toggle">{isExpanded('registration') ? '[-]' : '[+]'}</span>
                            </div>
                            {isExpanded('registration') && (
                                <div className="data-section-content">
                                    <div className="reg-section">
                                        {reg.registrar && (
                                            <div className="reg-row">
                                                <span className="reg-label">Registrar</span>
                                                <span className="reg-value">{reg.registrar}</span>
                                            </div>
                                        )}
                                        {reg.registrationDate && (
                                            <div className="reg-row">
                                                <span className="reg-label">Registered</span>
                                                <span className="reg-value">{new Date(reg.registrationDate).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        {reg.expirationDate && (
                                            <div className="reg-row">
                                                <span className="reg-label">Expires</span>
                                                <span className={`reg-value ${expiryClass}`}>
                                                    {new Date(reg.expirationDate).toLocaleDateString()}
                                                    {daysUntilExpiry !== null && (
                                                        <span className="reg-expiry-badge">
                                                            {daysUntilExpiry > 0 ? `${daysUntilExpiry} days remaining` : `Expired ${Math.abs(daysUntilExpiry)} days ago`}
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {reg.lastChangedDate && (
                                            <div className="reg-row">
                                                <span className="reg-label">Last Changed</span>
                                                <span className="reg-value">{new Date(reg.lastChangedDate).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        {status.length > 0 && (
                                            <div className="reg-row">
                                                <span className="reg-label">Status</span>
                                                <span className="reg-value">
                                                    {status.map((s, i) => (
                                                        <span key={i} className="reg-status-badge">{s.replace(/https?:\/\/\S+/g, '').trim()}</span>
                                                    ))}
                                                </span>
                                            </div>
                                        )}
                                        {nameservers.length > 0 && (
                                            <div className="reg-row">
                                                <span className="reg-label">Nameservers</span>
                                                <span className="reg-value mono">{nameservers.join(', ')}</span>
                                            </div>
                                        )}
                                        {reg.rdapServer && (
                                            <div className="reg-row">
                                                <span className="reg-label">Source</span>
                                                <span className="reg-value">
                                                    {source.toUpperCase()}
                                                    {reg.rdapServer && <span className="reg-source-detail"> ({new URL(reg.rdapServer).hostname})</span>}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Technologies Section (promoted to first-class) */}
                {(() => {
                    const stageData = getStageData();
                    const rawTechData = stageData.technologies || [];
                    const normalizedTechs = stageData.normalizedTechnologies || [];
                    const techSummary = stageData.techSummary || null;
                    const hasNormalized = normalizedTechs.length > 0;
                    const displayCount = hasNormalized ? normalizedTechs.length : rawTechData.length;

                    if (displayCount === 0) {
                        return (
                            <div className="data-section tech-section-promoted" id="data-section-technologies">
                                <div className="data-section-header">
                                    <h3 className="data-section-title">
                                        <span className="section-icon">&#9881;&#65039;</span>
                                        Technologies
                                        <span className="section-count">(0 detected)</span>
                                    </h3>
                                </div>
                                <div className="data-section-content">
                                    <div className="tech-empty-state">
                                        No technologies detected. Run a discovery scan to fingerprint your technology stack.
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    // ── Normalized view (new pipeline data available) ──
                    if (hasNormalized) {
                        // Group by top-level group
                        const grouped = {};
                        normalizedTechs.forEach(tech => {
                            const group = tech.group || 'Other';
                            if (!grouped[group]) grouped[group] = [];
                            grouped[group].push(tech);
                        });

                        // Sort groups in canonical order
                        const GROUP_ORDER = ['Infrastructure', 'Application Stack', 'Security Posture', 'Third-Party Services', 'Other'];
                        const sortedGroups = Object.keys(grouped).sort((a, b) => {
                            const ia = GROUP_ORDER.indexOf(a);
                            const ib = GROUP_ORDER.indexOf(b);
                            return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                        });

                        // Risk color helper
                        const riskColor = (level) => {
                            switch (level) {
                                case 'critical': return 'var(--severity-critical, #ff4444)';
                                case 'high': return 'var(--severity-high, #ff8800)';
                                case 'medium': return 'var(--severity-medium, #ffbb33)';
                                case 'low': return 'var(--severity-low, #00C851)';
                                default: return 'var(--accent-primary, #00D9FF)';
                            }
                        };

                        // Health score color
                        const healthColor = (score) => {
                            if (score >= 80) return 'var(--severity-low, #00C851)';
                            if (score >= 60) return 'var(--severity-medium, #ffbb33)';
                            if (score >= 40) return 'var(--severity-high, #ff8800)';
                            return 'var(--severity-critical, #ff4444)';
                        };

                        return (
                            <div className="data-section tech-section-promoted" id="data-section-technologies">
                                <div className="data-section-header">
                                    <h3 className="data-section-title">
                                        <span className="section-icon">&#9881;&#65039;</span>
                                        Technologies
                                        <span className="section-count">({displayCount} detected)</span>
                                    </h3>
                                    <div className="tech-header-actions">
                                        <button
                                            className="tech-history-btn"
                                            onClick={() => {
                                                // CSV export — client-side generation
                                                const csvHeader = 'Name,Version,Category,Group,Risk Level,EOL,CVE Count,Sources,First Seen,Detected On';
                                                const csvRows = normalizedTechs.map(t => {
                                                    const detectedOnStr = (t.detectedOn || []).map(d => d.subdomain || d.host || '').filter(Boolean).join('; ');
                                                    return [
                                                        `"${(t.name || '').replace(/"/g, '""')}"`,
                                                        `"${(t.version || '').replace(/"/g, '""')}"`,
                                                        `"${t.category || ''}"`,
                                                        `"${t.group || ''}"`,
                                                        t.risk?.level || 'info',
                                                        t.risk?.isEol ? 'Yes' : 'No',
                                                        t.risk?.cves?.length || 0,
                                                        `"${(t.sources || []).join(', ')}"`,
                                                        t.firstSeen || '',
                                                        `"${detectedOnStr}"`
                                                    ].join(',');
                                                });
                                                const csv = [csvHeader, ...csvRows].join('\n');
                                                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                                                const url = URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = `technologies-${asset?.value || assetId}-${new Date().toISOString().split('T')[0]}.csv`;
                                                a.click();
                                                URL.revokeObjectURL(url);
                                            }}
                                            title="Download technology inventory as CSV"
                                        >
                                            CSV
                                        </button>
                                        <button
                                            className="tech-history-btn"
                                            onClick={() => {
                                                setShowTechHistory(!showTechHistory);
                                                if (!showTechHistory && techHistoryEvents.length === 0) {
                                                    loadTechHistory();
                                                }
                                            }}
                                        >
                                            {showTechHistory ? 'Current' : 'History'}
                                        </button>
                                    </div>
                                </div>

                                {/* Technology History Timeline */}
                                {showTechHistory && (
                                    <div className="data-section-content tech-history-timeline">
                                        {techHistoryLoading && (
                                            <div className="tech-history-loading">Loading technology history...</div>
                                        )}
                                        {!techHistoryLoading && techHistoryEvents.length === 0 && (
                                            <div className="tech-history-empty">
                                                No technology changes recorded yet. Changes will appear here after subsequent scans detect differences in your technology stack.
                                            </div>
                                        )}
                                        {!techHistoryLoading && techHistoryEvents.length > 0 && (
                                            <div className="tech-history-list">
                                                {techHistoryEvents.map((event, idx) => {
                                                    const date = new Date(event.createdAt);
                                                    const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                                    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

                                                    // Determine event type icon and label
                                                    let typeIcon = '~';
                                                    let typeLabel = 'Changed';
                                                    let typeClass = 'changed';
                                                    if (event.ruleName?.toLowerCase().includes('new') || event.ruleName?.toLowerCase().includes('detected')) {
                                                        typeIcon = '+';
                                                        typeLabel = 'New';
                                                        typeClass = 'new';
                                                    } else if (event.ruleName?.toLowerCase().includes('removed')) {
                                                        typeIcon = '-';
                                                        typeLabel = 'Removed';
                                                        typeClass = 'removed';
                                                    } else if (event.ruleName?.toLowerCase().includes('version')) {
                                                        typeIcon = '^';
                                                        typeLabel = 'Version Change';
                                                        typeClass = 'version';
                                                    }

                                                    return (
                                                        <div key={event.eventId || idx} className={`tech-history-item tech-history-${typeClass}`}>
                                                            <div className="tech-history-date">
                                                                <span className="tech-history-date-text">{dateStr}</span>
                                                                <span className="tech-history-time-text">{timeStr}</span>
                                                            </div>
                                                            <div className="tech-history-indicator">
                                                                <span className={`tech-history-icon tech-history-icon-${typeClass}`}>{typeIcon}</span>
                                                                <div className="tech-history-line" />
                                                            </div>
                                                            <div className="tech-history-content">
                                                                <span className={`tech-history-type-badge tech-history-badge-${typeClass}`}>{typeLabel}</span>
                                                                <span className="tech-history-detail">{event.detail || event.ruleName}</span>
                                                                <span className={`tech-history-severity tech-history-sev-${event.ruleSeverity}`}>{event.ruleSeverity}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!showTechHistory && <div className="data-section-content">
                                    {/* Tech Health Score Bar */}
                                    {techSummary && techSummary.techHealthScore != null && (
                                        <div className="tech-health-bar">
                                            <div className="tech-health-header">
                                                <span className="tech-health-label">Tech Health</span>
                                                <span className="tech-health-score" style={{ color: healthColor(techSummary.techHealthScore) }}>
                                                    {techSummary.techHealthScore}/100
                                                </span>
                                            </div>
                                            <div className="tech-health-track">
                                                <div
                                                    className="tech-health-fill"
                                                    style={{
                                                        width: `${techSummary.techHealthScore}%`,
                                                        background: healthColor(techSummary.techHealthScore)
                                                    }}
                                                />
                                            </div>
                                            <div className="tech-health-meta">
                                                {techSummary.totalTechnologies} detected
                                                {techSummary.outdatedCount > 0 && <> &middot; {techSummary.outdatedCount} outdated</>}
                                                {techSummary.eolCount > 0 && <> &middot; {techSummary.eolCount} EOL</>}
                                                {techSummary.cveExposedCount > 0 && <> &middot; {techSummary.cveExposedCount} with CVEs</>}
                                            </div>
                                        </div>
                                    )}

                                    {/* Risk summary pills */}
                                    {techSummary && (
                                        <div className="tech-risk-pills">
                                            {techSummary.riskBreakdown.critical > 0 && (
                                                <span className="tech-risk-pill tech-risk-critical">{techSummary.riskBreakdown.critical} Critical</span>
                                            )}
                                            {techSummary.riskBreakdown.high > 0 && (
                                                <span className="tech-risk-pill tech-risk-high">{techSummary.riskBreakdown.high} High</span>
                                            )}
                                            {techSummary.riskBreakdown.medium > 0 && (
                                                <span className="tech-risk-pill tech-risk-medium">{techSummary.riskBreakdown.medium} Medium</span>
                                            )}
                                            {(techSummary.riskBreakdown.low > 0 || techSummary.riskBreakdown.info > 0) && (
                                                <span className="tech-risk-pill tech-risk-info">{(techSummary.riskBreakdown.low || 0) + (techSummary.riskBreakdown.info || 0)} OK</span>
                                            )}
                                        </div>
                                    )}

                                    {/* Categorized technology groups */}
                                    {sortedGroups.map(group => (
                                        <div key={group} className="tech-section-category">
                                            <h4 className="tech-section-cat-title">
                                                {group}
                                                <span className="tech-group-count">({grouped[group].length})</span>
                                            </h4>
                                            <div className="tech-badges">
                                                {grouped[group]
                                                    .sort((a, b) => {
                                                        const riskOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
                                                        return (riskOrder[a.risk?.level] ?? 5) - (riskOrder[b.risk?.level] ?? 5);
                                                    })
                                                    .map((tech, i) => {
                                                    const techKey = `${group}-${i}`;
                                                    const isExpanded = expandedTechItems[techKey];
                                                    return (
                                                    <div key={i} className="tech-badge-wrapper">
                                                        <span
                                                            className={`tech-badge tech-risk-${tech.risk?.level || 'info'}${tech.detectedOn?.length > 0 ? ' tech-badge-expandable' : ''}`}
                                                            title={[
                                                                tech.category,
                                                                tech.risk?.isEol ? 'EOL' : null,
                                                                tech.risk?.isOutdated ? 'Outdated' : null,
                                                                tech.risk?.cves?.length ? `${tech.risk.cves.length} CVEs` : null,
                                                                tech.risk?.isInternetFacingDb ? 'Internet-facing DB' : null,
                                                                tech.sources?.length ? `Sources: ${tech.sources.join(', ')}` : null
                                                            ].filter(Boolean).join(' · ')}
                                                            onClick={() => tech.detectedOn?.length > 0 && setExpandedTechItems(prev => ({ ...prev, [techKey]: !prev[techKey] }))}
                                                            style={tech.detectedOn?.length > 0 ? { cursor: 'pointer' } : undefined}
                                                        >
                                                            {tech.risk?.level && tech.risk.level !== 'info' && (
                                                                <span className="tech-risk-dot" style={{ background: riskColor(tech.risk.level) }} />
                                                            )}
                                                            {tech.name}{tech.version ? ` ${tech.version}` : ''}
                                                            {tech.risk?.isEol && <span className="tech-flag tech-flag-eol">EOL</span>}
                                                            {tech.risk?.cves?.length > 0 && <span className="tech-flag tech-flag-cve">{tech.risk.cves.length} CVE{tech.risk.cves.length > 1 ? 's' : ''}</span>}
                                                            {tech.risk?.isInternetFacingDb && <span className="tech-flag tech-flag-db">DB Exposed</span>}
                                                            {tech.detectedOn?.length > 0 && <span className="tech-expand-icon">{isExpanded ? '\u25BC' : '\u25B6'}</span>}
                                                        </span>
                                                        {isExpanded && tech.detectedOn?.length > 0 && (
                                                            <div className="tech-subdomain-breakdown">
                                                                {tech.detectedOn.map((loc, li) => (
                                                                    <div key={li} className="tech-subdomain-item">
                                                                        <span className="tech-subdomain-tree">{li < tech.detectedOn.length - 1 ? '\u251C\u2500' : '\u2514\u2500'}</span>
                                                                        <span className="tech-subdomain-name">{loc.subdomain || loc.host || 'unknown'}</span>
                                                                        {loc.port && <span className="tech-subdomain-port">:{loc.port}</span>}
                                                                        {loc.urls?.length > 0 && <span className="tech-subdomain-urls">{loc.urls.length} URL{loc.urls.length > 1 ? 's' : ''}</span>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>}
                            </div>
                        );
                    }

                    // ── Fallback: legacy badge display (pre-normalization scan data) ──
                    const TECH_CATEGORIES = {
                        'Web Servers': ['nginx', 'apache', 'iis', 'caddy', 'lighttpd', 'litespeed'],
                        'Frameworks & CMS': ['wordpress', 'drupal', 'joomla', 'react', 'angular', 'vue', 'next.js', 'nuxt', 'django', 'flask', 'express', 'laravel', 'rails'],
                        'Languages': ['php', 'python', 'node.js', 'java', 'ruby', 'go', 'asp.net'],
                        'Databases': ['mysql', 'postgresql', 'mongodb', 'redis', 'elasticsearch', 'mssql'],
                        'CDN & Security': ['cloudflare', 'akamai', 'fastly', 'cloudfront', 'incapsula', 'sucuri'],
                        'JavaScript Libraries': ['jquery', 'bootstrap', 'lodash', 'moment.js', 'axios'],
                        'Analytics & Marketing': ['google analytics', 'google tag manager', 'facebook pixel', 'hotjar'],
                    };

                    const categorize = (name) => {
                        const lower = name.toLowerCase();
                        for (const [cat, keywords] of Object.entries(TECH_CATEGORIES)) {
                            if (keywords.some(kw => lower.includes(kw))) return cat;
                        }
                        return 'Other';
                    };

                    const grouped = {};
                    rawTechData.forEach(tech => {
                        const techName = typeof tech === 'string' ? tech : (tech.name || tech.technology || String(tech));
                        const cat = (typeof tech === 'object' && tech.category) ? tech.category : categorize(techName);
                        if (!grouped[cat]) grouped[cat] = [];
                        grouped[cat].push(techName);
                    });

                    const sortedCats = Object.keys(grouped).sort((a, b) => {
                        if (a === 'Other') return 1;
                        if (b === 'Other') return -1;
                        return a.localeCompare(b);
                    });

                    return (
                        <div className="data-section tech-section-promoted" id="data-section-technologies">
                            <div className="data-section-header">
                                <h3 className="data-section-title">
                                    <span className="section-icon">&#9881;&#65039;</span>
                                    Technologies
                                    <span className="section-count">({rawTechData.length} detected)</span>
                                </h3>
                            </div>
                            <div className="data-section-content">
                                <div className="tech-rescan-hint">
                                    Re-scan this asset to see categorized technology data with risk analysis and health scoring.
                                </div>
                                {sortedCats.map(cat => (
                                    <div key={cat} className="tech-section-category">
                                        <h4 className="tech-section-cat-title">{cat}</h4>
                                        <div className="tech-badges">
                                            {grouped[cat].map((name, i) => (
                                                <span key={i} className={`tech-badge tech-cat-${cat.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                                                    {name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                {/* URLs Section */}
                <div className="data-section" id="data-section-urls">
                    <div
                        className="data-section-header"
                        onClick={() => toggleDataSection('urls')}
                    >
                        <h3 className="data-section-title">
                            <span className="section-icon">🔗</span>
                            URLs / Endpoints
                            <span className="section-count">({getStageData().urls?.length || asset.urlsScanned || asset.webAppCount || 0})</span>
                        </h3>
                        <span className="section-toggle">{isExpanded('urls') ? '[-]' : '[+]'}</span>
                    </div>
                    {isExpanded('urls') && (
                        <div className="data-section-content">
                            {detailsLoading ? (
                                <div className="section-loading">Loading URL data...</div>
                            ) : (
                                <>
                                    {(() => {
                                        const data = getStageData();
                                        const urls = data.urls || [];
                                        const webServers = data.webServers || [];
                                        const hasData = urls.length > 0 || webServers.length > 0;
                                        const httpProbes = scanDetails?.fullResult?.web?.httpProbes ||
                                                          scanDetails?.fullResult?.stageResults?.webCrawling?.httpProbes || [];

                                        // Build probe lookup by URL
                                        const probeMap = new Map();
                                        httpProbes.forEach(probe => {
                                            if (probe.url) probeMap.set(probe.url, probe);
                                        });

                                        return hasData ? (
                                            <>
                                                {/* URLs with expandable HTTP probe details */}
                                                <div className="urls-section">
                                                    <div className="data-list urls-list">
                                                        {(urls.length > 0 ? urls : webServers).slice(0, 50).map((url, i) => {
                                                            const urlStr = typeof url === 'string' ? url : (url.url || url.host || '');
                                                            const probe = probeMap.get(urlStr);
                                                            const hasProbe = !!probe;

                                                            return (
                                                                <div key={i} className="http-probe-row">
                                                                    <div className="data-item url-item">
                                                                        <span className="data-value mono">{urlStr}</span>
                                                                        {hasProbe && probe.statusCode && (
                                                                            <span className={`http-probe-status http-probe-status-${Math.floor(probe.statusCode / 100)}xx`}>
                                                                                {probe.statusCode}
                                                                            </span>
                                                                        )}
                                                                        {hasProbe && probe.serverHeader && (
                                                                            <span className="http-probe-server">{probe.serverHeader}</span>
                                                                        )}
                                                                        {hasProbe && probe.title && (
                                                                            <span className="http-probe-title">{probe.title}</span>
                                                                        )}
                                                                    </div>
                                                                    {hasProbe && probe.redirectUrl && (
                                                                        <div className="http-probe-redirect">
                                                                            &rarr; {probe.redirectUrl}
                                                                        </div>
                                                                    )}
                                                                    {hasProbe && probe.technologies && probe.technologies.length > 0 && (
                                                                        <div className="http-probe-techs">
                                                                            {probe.technologies.map((t, ti) => (
                                                                                <span key={ti} className="tech-badge tech-badge-sm">{typeof t === 'string' ? t : t.name || t}</span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                        {urls.length > 50 && (
                                                            <div className="data-more">...and {urls.length - 50} more</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="section-empty">No URL data available. Run a discovery scan to detect web endpoints.</div>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Cloud Discovery Section */}
                <div className="data-section cloud-section" id="data-section-cloud">
                    <div
                        className="data-section-header"
                        onClick={() => toggleDataSection('cloud')}
                    >
                        <h3 className="data-section-title">
                            <span className="section-icon">☁️</span>
                            Cloud Discovery
                            <span className="section-count">({getStageData().cloud?.summary?.total || 0})</span>
                        </h3>
                        <span className="section-toggle">{isExpanded('cloud') ? '[-]' : '[+]'}</span>
                    </div>
                    {isExpanded('cloud') && (
                        <div className="data-section-content">
                            {detailsLoading ? (
                                <div className="section-loading">Loading cloud data...</div>
                            ) : (
                                <>
                                    {(() => {
                                        const data = getStageData();
                                        const cloud = data.cloud || {};
                                        const summary = cloud.summary || {};
                                        const hasData = summary.total > 0 || cloud.azure?.tenants?.length > 0 || cloud.azure?.tenantId;

                                        if (!hasData) {
                                            return (
                                                <div className="section-empty">
                                                    No cloud resources discovered during scan.
                                                    <div className="cloud-tools-link">
                                                        <span className="tools-hint">Use the <a href="/tools" className="tools-link">Tools page</a> to search with custom keywords.</span>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <>
                                                {/* Disclaimer Banner */}
                                                <div className="cloud-disclaimer">
                                                    <span className="disclaimer-icon">ℹ️</span>
                                                    <span className="disclaimer-text">
                                                        These results are based on keyword matching and require validation.
                                                        Use the <a href="/tools" className="tools-link">Tools page</a> to search with custom keywords.
                                                    </span>
                                                </div>

                                                {/* Summary Stats */}
                                                {summary.total > 0 && (
                                                    <div className="cloud-summary">
                                                        <span className="summary-stat">
                                                            Total Resources: <strong>{summary.total}</strong>
                                                        </span>
                                                        {summary.publicReadCount > 0 && (
                                                            <span className="summary-stat warning">
                                                                Public Read: <strong>{summary.publicReadCount}</strong>
                                                            </span>
                                                        )}
                                                        {summary.publicWriteCount > 0 && (
                                                            <span className="summary-stat critical">
                                                                Public Write: <strong>{summary.publicWriteCount}</strong>
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Azure Tenant Discovery */}
                                                {(cloud.azure?.tenants?.length > 0 || cloud.azure?.tenantId) && (
                                                    <div className="cloud-provider-section">
                                                        <h4 className="provider-header azure">
                                                            <span className="provider-icon">🔷</span>
                                                            Azure Tenant Discovery
                                                        </h4>
                                                        <div className="tenant-list">
                                                            {cloud.azure.tenants?.map((tenant, i) => (
                                                                <div key={i} className="tenant-item">
                                                                    <span className="tenant-label">Domain:</span>
                                                                    <code className="tenant-domain">{tenant.domain}</code>
                                                                    <span className="tenant-label">Tenant ID:</span>
                                                                    <code className="tenant-id">{tenant.tenantId}</code>
                                                                </div>
                                                            ))}
                                                            {!cloud.azure.tenants?.length && cloud.azure.tenantId && (
                                                                <div className="tenant-item">
                                                                    <span className="tenant-label">Tenant ID:</span>
                                                                    <code className="tenant-id">{cloud.azure.tenantId}</code>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Azure Blob Containers */}
                                                {cloud.azure?.containers?.length > 0 && (
                                                    <div className="cloud-provider-section">
                                                        <h4 className="provider-header azure">
                                                            <span className="provider-icon">🔷</span>
                                                            Azure Blob Storage ({cloud.azure.containers.length})
                                                        </h4>
                                                        <div className="bucket-list">
                                                            {cloud.azure.containers.slice(0, 20).map((container, i) => (
                                                                <div key={i} className={`bucket-item access-${container.access}`}>
                                                                    <span className="bucket-name">
                                                                        {container.account}{container.container && `/${container.container}`}
                                                                    </span>
                                                                    <span className={`access-badge access-${container.access}`}>
                                                                        {container.access}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                            {cloud.azure.containers.length > 20 && (
                                                                <div className="data-more">...and {cloud.azure.containers.length - 20} more</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* AWS S3 Buckets */}
                                                {cloud.aws?.buckets?.length > 0 && (
                                                    <div className="cloud-provider-section">
                                                        <h4 className="provider-header aws">
                                                            <span className="provider-icon">🟠</span>
                                                            AWS S3 Buckets ({cloud.aws.buckets.length})
                                                        </h4>
                                                        <div className="bucket-list">
                                                            {cloud.aws.buckets.slice(0, 20).map((bucket, i) => (
                                                                <div key={i} className={`bucket-item access-${bucket.access}`}>
                                                                    <span className="bucket-name">{bucket.name}</span>
                                                                    <span className={`access-badge access-${bucket.access}`}>
                                                                        {bucket.access}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                            {cloud.aws.buckets.length > 20 && (
                                                                <div className="data-more">...and {cloud.aws.buckets.length - 20} more</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* GCP Storage Buckets */}
                                                {cloud.gcp?.buckets?.length > 0 && (
                                                    <div className="cloud-provider-section">
                                                        <h4 className="provider-header gcp">
                                                            <span className="provider-icon">🔵</span>
                                                            GCP Cloud Storage ({cloud.gcp.buckets.length})
                                                        </h4>
                                                        <div className="bucket-list">
                                                            {cloud.gcp.buckets.slice(0, 20).map((bucket, i) => (
                                                                <div key={i} className={`bucket-item access-${bucket.access}`}>
                                                                    <span className="bucket-name">{bucket.name}</span>
                                                                    <span className={`access-badge access-${bucket.access}`}>
                                                                        {bucket.access}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                            {cloud.gcp.buckets.length > 20 && (
                                                                <div className="data-more">...and {cloud.gcp.buckets.length - 20} more</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Vulnerabilities Section */}
                <div className="data-section vulnerabilities-section" id="data-section-vulnerabilities">
                    <div
                        className="data-section-header"
                        onClick={() => toggleDataSection('vulnerabilities')}
                    >
                        <h3 className="data-section-title">
                            <span className="section-icon">⚠️</span>
                            Vulnerabilities
                            <span className="section-count">({totalVulns})</span>
                        </h3>
                        <span className="section-toggle">{isExpanded('vulnerabilities') ? '[-]' : '[+]'}</span>
                    </div>
                    {isExpanded('vulnerabilities') && (
                        <div className="data-section-content">
                            {/* Severity Filters */}
                            <div className="vuln-filters">
                                <button
                                    className={`vuln-filter-btn ${vulnFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => setVulnFilter('all')}
                                >
                                    All ({totalVulns})
                                </button>
                                <button
                                    className={`vuln-filter-btn critical ${vulnFilter === 'critical' ? 'active' : ''}`}
                                    onClick={() => setVulnFilter('critical')}
                                >
                                    Critical ({vulnCounts.critical})
                                </button>
                                <button
                                    className={`vuln-filter-btn high ${vulnFilter === 'high' ? 'active' : ''}`}
                                    onClick={() => setVulnFilter('high')}
                                >
                                    High ({vulnCounts.high})
                                </button>
                                <button
                                    className={`vuln-filter-btn medium ${vulnFilter === 'medium' ? 'active' : ''}`}
                                    onClick={() => setVulnFilter('medium')}
                                >
                                    Medium ({vulnCounts.medium})
                                </button>
                                <button
                                    className={`vuln-filter-btn low ${vulnFilter === 'low' ? 'active' : ''}`}
                                    onClick={() => setVulnFilter('low')}
                                >
                                    Low ({vulnCounts.low})
                                </button>
                                <button
                                    className={`vuln-filter-btn info ${vulnFilter === 'info' ? 'active' : ''}`}
                                    onClick={() => setVulnFilter('info')}
                                >
                                    Info ({vulnCounts.info})
                                </button>
                            </div>

                            {detailsLoading ? (
                                <div className="section-loading">Loading vulnerability data...</div>
                            ) : (
                                <>
                                    {(() => {
                                        const data = getStageData();
                                        let vulns = data.vulnerabilities || [];

                                        // Filter by severity (before consolidation to maintain counts)
                                        if (vulnFilter !== 'all') {
                                            vulns = vulns.filter(v =>
                                                (v.severity || v.priority || '').toLowerCase() === vulnFilter
                                            );
                                        }

                                        // Consolidate similar vulnerabilities
                                        vulns = consolidateVulnerabilities(vulns);

                                        // Sort by severity
                                        const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
                                        vulns = [...vulns].sort((a, b) => {
                                            const aOrder = severityOrder[(a.severity || a.priority || 'info').toLowerCase()] || 5;
                                            const bOrder = severityOrder[(b.severity || b.priority || 'info').toLowerCase()] || 5;
                                            return aOrder - bOrder;
                                        });

                                        return vulns.length > 0 ? (
                                            <div className="vuln-list">
                                                {vulns.slice(0, 25).map((vuln, i) => {
                                                    const severity = (vuln.severity || vuln.priority || 'info').toLowerCase();
                                                    const vulnId = `vuln-${i}`;
                                                    const isExpanded = expandedVulnItems[vulnId];
                                                    const isGroupExpanded = vuln.isGroup && expandedVulnGroups[vulnId];
                                                    const findingIdentifier = vuln.templateId || vuln.cve || vuln.cveId || vuln.id || vuln.name;
                                                    const suppression = isSuppressed('vulnerability', findingIdentifier);
                                                    return (
                                                        <div
                                                            key={i}
                                                            className={`vuln-card ${severity} ${isExpanded ? 'expanded' : ''} ${suppression ? 'suppressed' : ''}`}
                                                            style={{ borderLeftColor: getSeverityColor(severity) }}
                                                        >
                                                            <div
                                                                className="vuln-header clickable"
                                                                onClick={() => toggleVulnItem(vulnId)}
                                                            >
                                                                <div className="vuln-header-left">
                                                                    <span
                                                                        className="vuln-severity"
                                                                        style={{ color: getSeverityColor(severity) }}
                                                                    >
                                                                        {severity.toUpperCase()}
                                                                    </span>
                                                                    <span className="vuln-name">{vuln.name || vuln.title || vuln.id || 'Unknown'}</span>
                                                                    {suppression && (
                                                                        <span className={`disposition-inline-badge ${suppression.disposition === 'ignore' ? 'disposition-ignore' : 'disposition-accept'}`}>
                                                                            {suppression.disposition === 'ignore' ? 'Ignored' : 'Accepted'}
                                                                            {suppression.expiresAt && (
                                                                                <span className="expiry-hint"> ({Math.max(0, Math.round((new Date(suppression.expiresAt) - new Date()) / (1000*60*60*24)))}d)</span>
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="vuln-header-actions">
                                                                    {suppression ? (
                                                                        <span className={`disposition-inline-badge ${suppression.disposition === 'ignore' ? 'disposition-ignore' : 'disposition-accept'}`}>
                                                                            {suppression.disposition === 'ignore' ? 'Ignored' : 'Accepted'}
                                                                            {suppression.expiresAt && (
                                                                                <span className="expiry-hint"> ({Math.max(0, Math.round((new Date(suppression.expiresAt) - new Date()) / (1000*60*60*24)))}d)</span>
                                                                            )}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="disposition-buttons">
                                                                            <button
                                                                                className="btn-accept-inline"
                                                                                onClick={(e) => openDispositionModal(
                                                                                    'accept',
                                                                                    'vulnerability',
                                                                                    findingIdentifier,
                                                                                    vuln.name || vuln.title || vuln.id || 'Unknown',
                                                                                    vuln,
                                                                                    e
                                                                                )}
                                                                                title="Accept: Known risk, still counts in score"
                                                                            >
                                                                                Accept
                                                                            </button>
                                                                            <button
                                                                                className="btn-ignore-inline"
                                                                                onClick={(e) => openDispositionModal(
                                                                                    'ignore',
                                                                                    'vulnerability',
                                                                                    findingIdentifier,
                                                                                    vuln.name || vuln.title || vuln.id || 'Unknown',
                                                                                    vuln,
                                                                                    e
                                                                                )}
                                                                                title="Ignore: False positive, removed from score"
                                                                            >
                                                                                Ignore
                                                                            </button>
                                                                        </span>
                                                                    )}
                                                                    <span className="vuln-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                                                                </div>
                                                            </div>
                                                            <div className="vuln-target">
                                                                <span className="target-label">Target:</span>
                                                                <span className="target-value">{vuln.host || vuln.matchedAt || vuln.url || 'N/A'}</span>
                                                            </div>
                                                            {isExpanded && (
                                                                <div className="vuln-details">
                                                                    {vuln.description && (
                                                                        <div className="vuln-detail-row">
                                                                            <span className="detail-label">Description:</span>
                                                                            <span className="detail-value">{vuln.description}</span>
                                                                        </div>
                                                                    )}
                                                                    {vuln.matchedAt && (
                                                                        <div className="vuln-detail-row">
                                                                            <span className="detail-label">Matched At:</span>
                                                                            <span className="detail-value mono">{vuln.matchedAt}</span>
                                                                        </div>
                                                                    )}
                                                                    {vuln.templateId && (
                                                                        <div className="vuln-detail-row">
                                                                            <span className="detail-label">Template:</span>
                                                                            <span className="detail-value mono">{vuln.templateId}</span>
                                                                        </div>
                                                                    )}
                                                                    <div className="vuln-meta">
                                                                        {vuln.host && <span className="vuln-tag">{vuln.host}</span>}
                                                                        {vuln.port && <span className="vuln-tag">Port {vuln.port}</span>}
                                                                        {(vuln.cve || vuln.cveId) && <span className="vuln-tag cve">{vuln.cve || vuln.cveId}</span>}
                                                                        {(vuln.tool || vuln.source) && <span className="vuln-tag tool">{vuln.tool || vuln.source}</span>}
                                                                        {vuln.tags && vuln.tags.slice(0, 5).map((tag, ti) => (
                                                                            <span key={ti} className="vuln-tag">{tag}</span>
                                                                        ))}
                                                                    </div>

                                                                    {/* HTTP Transaction Section — shared component */}
                                                                    <HttpTransactionView httpTransaction={vuln.httpTransaction} />

                                                                    {/* Grouped Items (for consolidated vulnerabilities) */}
                                                                    {vuln.isGroup && vuln.groupItems && (
                                                                        <div className="vuln-group-items">
                                                                            <div
                                                                                className="vuln-group-toggle"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleVulnGroup(vulnId);
                                                                                }}
                                                                            >
                                                                                <span>{isGroupExpanded ? '▼' : '▶'} View {vuln.groupItems.length} individual findings</span>
                                                                            </div>
                                                                            {isGroupExpanded && (
                                                                                <div className="vuln-group-list">
                                                                                    {vuln.groupItems.map((item, gi) => (
                                                                                        <div key={gi} className="vuln-group-item">
                                                                                            <span className="group-item-name">{item.name || item.title || item.id}</span>
                                                                                            <span className="group-item-target">{item.matchedAt || item.host || 'N/A'}</span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {vulns.length > 25 && (
                                                    <div className="data-more">...and {vulns.length - 25} more vulnerabilities</div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="section-empty">
                                                {vulnFilter === 'all'
                                                    ? 'No vulnerabilities detected. Run a scan to check for vulnerabilities.'
                                                    : `No ${vulnFilter} severity vulnerabilities found.`
                                                }
                                            </div>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* PRD-63 WP3: Unified Accept/Ignore modals — replaces the inline
                suppression modal. Same shapes used elsewhere in the app, so the
                disposition UX is identical regardless of entry point. */}
            <AcceptAlarmModal
                isOpen={acceptModalOpen}
                onClose={() => setAcceptModalOpen(false)}
                finding={acceptModalFinding}
                assetId={assetId}
                defaultScope="host"
                onSuccess={(result) => {
                    handleDispositionSuccess(
                        result,
                        'Accepted',
                        acceptModalFinding?.findingName || acceptModalFinding?.name || 'finding'
                    );
                    setAcceptModalOpen(false);
                }}
            />
            <IgnoreAlarmModal
                isOpen={ignoreModalOpen}
                onClose={() => setIgnoreModalOpen(false)}
                finding={ignoreModalFinding}
                assetId={assetId}
                defaultScope="host"
                onSuccess={(result) => {
                    handleDispositionSuccess(
                        result,
                        'Ignored',
                        ignoreModalFinding?.findingName || ignoreModalFinding?.name || 'finding'
                    );
                    setIgnoreModalOpen(false);
                }}
            />
        </>
    );
}

export default AssetDataSections;
